import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import HotelCard from '../components/HotelCard';
import { getApiUrl } from '../config/api';
import './PropertiesPage.css';

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(true); // Start with minimized header
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const handleBackClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const filteredProperties = searchQuery.trim()
    ? properties.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (p.city && p.city.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (p.address && p.address.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : properties;

  // Check if user is logged in
  const isUserLoggedIn = () => {
    try {
      const user = localStorage.getItem('user');
      return user !== null;
    } catch {
      return false;
    }
  };

  const handleViewDetails = (hotel) => {
    const propertyId = hotel._id || hotel.id;
    if (!isUserLoggedIn()) {
      navigate('/login');
    } else if (propertyId) {
      navigate(`/property/${propertyId}`);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const transformProperty = (property) => ({
      id: property._id || property.id,
      _id: property._id,
      name: property.name || 'Property',
      city: property.city || '',
      address: property.address || property.city || 'Location',
      images: property.images || [],
      rating: property.rating || 0,
      reviews: property.reviews || 0,
      pricePerNight: property.pricePerNight || 0,
      currency: property.currency || 'INR',
      type: property.type || 'Accommodation',
      distanceFromCenter: property.distanceFromCenter || null,
      source: property.source || 'internal',
      amenities: property.amenities || [],
      bedrooms: property.bedrooms || 0,
      bathrooms: property.bathrooms || 0,
      maxGuests: property.maxGuests || 0,
      ...property
    });

    const mergeById = (base, extra) => {
      const map = new Map();
      base.forEach((p) => map.set(p.id || p._id, p));
      extra.forEach((p) => map.set(p.id || p._id, p));
      return Array.from(map.values());
    };

    const loadFromLocalStorage = () => {
      try {
        const user = localStorage.getItem('user');
        const parsedUser = user ? JSON.parse(user) : null;
        const userId = parsedUser ? (parsedUser._id || parsedUser.id) : null;
        const keys = [
          userId ? `properties_${userId}` : null,
          'myProperties',
          'hostProperties'
        ].filter(Boolean);

        for (const key of keys) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const props = JSON.parse(raw);
            if (Array.isArray(props) && props.length > 0) {
              return props.map(transformProperty);
            }
          }
        }
      } catch (err) {
        console.error('Error loading properties from localStorage:', err);
      }
      return [];
    };

    const fetchAllProperties = async () => {
      try {
        setLoading(true);
        const url = getApiUrl('/api/properties/all');
        console.log('Fetching all host accommodations from:', url);
        const response = await fetch(url);
        console.log('Response status:', response.status);
        const result = await response.json();
        console.log('API Response:', result);

        let transformedProperties = [];

        if (result.success && Array.isArray(result.properties)) {
          transformedProperties = result.properties.map(transformProperty);
          console.log('Properties received:', transformedProperties.length);
        }

        if (transformedProperties.length === 0) {
          const token = localStorage.getItem('token');
          if (token) {
            const myPropsRes = await fetch(getApiUrl('/api/properties/my-properties'), {
              headers: { Authorization: `Bearer ${token}` }
            });
            const myPropsResult = await myPropsRes.json();
            if (myPropsResult.success && Array.isArray(myPropsResult.properties)) {
              const myProps = myPropsResult.properties.map(transformProperty);
              transformedProperties = mergeById(transformedProperties, myProps);
            }
          }
        }

        if (transformedProperties.length === 0) {
          const localProps = loadFromLocalStorage();
          if (localProps.length > 0) {
            transformedProperties = mergeById(transformedProperties, localProps);
          }
        }

        if (transformedProperties.length > 0) {
          console.log('Final properties:', transformedProperties.length);
          setProperties(transformedProperties);
        } else {
          console.log('No properties found');
          setProperties([]);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllProperties();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div className="properties-page">
      <Navbar isScrolled={isScrolled} />

      <section className="properties-hero">
        <div className="properties-hero-content">
          <motion.button
            type="button"
            className="properties-back-button"
            onClick={handleBackClick}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            {'< Back'}
          </motion.button>
          <motion.h1
            className="properties-hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            All Host Accommodations
          </motion.h1>
          <motion.p
            className="properties-hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Browse our complete collection of verified host properties
          </motion.p>
        </div>
      </section>

      <section className="properties-search-bar-wrap">
        <div className="properties-search-bar-inner">
          <span className="properties-search-icon" aria-hidden>🔍</span>
          <input
            type="search"
            className="properties-search-input"
            placeholder="Search by name, city, or address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search accommodations"
          />
          {searchQuery && (
            <button
              type="button"
              className="properties-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </section>

      <section className="properties-section">
        <div className="properties-container">
          {loading ? (
            <div className="loading-state">
              <p>Loading accommodations...</p>
            </div>
          ) : properties.length === 0 ? (
            <div className="empty-state">
              <p>No accommodations available at the moment.</p>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Please check back later or contact support.</p>
            </div>
          ) : (
            <>
              <div className="properties-count">
                <p>Showing <strong>{filteredProperties.length}</strong> {filteredProperties.length === 1 ? 'accommodation' : 'accommodations'}{searchQuery.trim() ? ` (filtered from ${properties.length})` : ''}</p>
              </div>

              <motion.div
                className="properties-grid"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {filteredProperties.length === 0 ? (
                  <div className="empty-state">
                    <p>No accommodations match &quot;{searchQuery}&quot;.</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Try a different search term.</p>
                  </div>
                ) : (
                  filteredProperties.map((property) => (
                  <motion.div
                    key={property.id}
                    variants={itemVariants}
                  >
                    <HotelCard
                      hotel={property}
                      buttonText="View Details"
                      onViewDetails={() => {
                        if (property._id) {
                          navigate(`/property/${property._id}`);
                        }
                      }}
                    />
                  </motion.div>
                  ))
                )}
              </motion.div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertiesPage;
